// src/components/aols/OLSstudent/LocationIndicator.tsx
// Updated to add Age, OLS Teacher OLS Inuo, and Experience/Status indicators with icons to Location Pre-evaluation
// Included Register OLS student CTA at /register/olssregister
// 06/03/25 08:10 PM +07
import React, { useState } from "react";
import { Table, Typography, Input, Tabs, Button } from "antd";
import { SearchOutlined, CalendarOutlined, TeamOutlined, StarOutlined } from "@ant-design/icons";
import { debounce } from "lodash";
import { LocationFee, LocationFactor } from "./types";
import CountriesIndicator from "./CountriesIndicator";
import { Link } from "react-router-dom";
import "./LocationIndicator.css";

const { Title, Text } = Typography;
const { TabPane } = Tabs;

const LocationIndicator: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [activeTab, setActiveTab] = useState<string>("us");

  // Debounce the search input to prevent excessive re-renders
  const debouncedSetSearchQuery = debounce((value: string) => {
    setSearchQuery(value);
  }, 300);

  // Clean up debounce on component unmount
  React.useEffect(() => {
    return () => {
      debouncedSetSearchQuery.cancel();
    };
  }, []);

  // US States sorted by GDP (2023 data, in billions USD, descending order)
  const usStatesByGdp: { state: string; gdp: number; fee: string }[] = [
    { state: "California", gdp: 3900, fee: "$41,000" },
    { state: "Texas", gdp: 2300, fee: "$39,000" },
    { state: "New York", gdp: 1900, fee: "$41,000" },
    { state: "Florida", gdp: 1400, fee: "$39,000" },
    { state: "Illinois", gdp: 1000, fee: "$39,000" },
    { state: "Pennsylvania", gdp: 900, fee: "$35,000" },
    { state: "Ohio", gdp: 800, fee: "$35,000" },
    { state: "Georgia", gdp: 700, fee: "$35,000" },
    { state: "New Jersey", gdp: 700, fee: "$41,000" },
    { state: "North Carolina", gdp: 700, fee: "$35,000" },
    { state: "Michigan", gdp: 600, fee: "$35,000" },
    { state: "Virginia", gdp: 600, fee: "$39,000" },
    { state: "Washington", gdp: 600, fee: "$35,000" },
    { state: "Massachusetts", gdp: 600, fee: "$39,000" },
    { state: "Indiana", gdp: 500, fee: "$35,000" },
    { state: "Arizona", gdp: 500, fee: "$35,000" },
    { state: "Tennessee", gdp: 500, fee: "$35,000" },
    { state: "Missouri", gdp: 400, fee: "$35,000" },
    { state: "Maryland", gdp: 400, fee: "$39,000" },
    { state: "Wisconsin", gdp: 400, fee: "$35,000" },
    { state: "Colorado", gdp: 400, fee: "$35,000" },
    { state: "Minnesota", gdp: 400, fee: "$35,000" },
    { state: "South Carolina", gdp: 300, fee: "$33,000" },
    { state: "Alabama", gdp: 300, fee: "$33,000" },
    { state: "Louisiana", gdp: 300, fee: "$33,000" },
    { state: "Kentucky", gdp: 300, fee: "$33,000" },
    { state: "Oregon", gdp: 300, fee: "$33,000" },
    { state: "Oklahoma", gdp: 200, fee: "$33,000" },
    { state: "Connecticut", gdp: 200, fee: "$35,000" },
    { state: "Utah", gdp: 200, fee: "$33,000" },
    { state: "Iowa", gdp: 200, fee: "$33,000" },
    { state: "Nevada", gdp: 200, fee: "$41,000" },
    { state: "Arkansas", gdp: 200, fee: "$33,000" },
    { state: "Mississippi", gdp: 100, fee: "$33,000" },
    { state: "Kansas", gdp: 100, fee: "$33,000" },
    { state: "New Mexico", gdp: 100, fee: "$33,000" },
    { state: "Nebraska", gdp: 100, fee: "$33,000" },
    { state: "Idaho", gdp: 100, fee: "$33,000" },
    { state: "West Virginia", gdp: 100, fee: "$33,000" },
    { state: "Hawaii", gdp: 100, fee: "$35,000" },
    { state: "New Hampshire", gdp: 100, fee: "$33,000" },
    { state: "Maine", gdp: 100, fee: "$33,000" },
    { state: "Montana", gdp: 50, fee: "$33,000" },
    { state: "Rhode Island", gdp: 50, fee: "$33,000" },
    { state: "Delaware", gdp: 50, fee: "$33,000" },
    { state: "South Dakota", gdp: 50, fee: "$33,000" },
    { state: "North Dakota", gdp: 50, fee: "$33,000" },
    { state: "Alaska", gdp: 50, fee: "$33,000" },
    { state: "Vermont", gdp: 50, fee: "$33,000" },
    { state: "Wyoming", gdp: 50, fee: "$33,000" },
  ];

  // Map cities to states for searchability
  const cityToStateMap: { [key: string]: string } = {
    "New York": "New York",
    "Los Angeles": "California",
    "San Francisco": "California",
    "Las Vegas": "Nevada",
    "Miami": "Florida",
    "Chicago": "Illinois",
    "Dallas": "Texas",
    "Houston": "Texas",
    "Boston": "Massachusetts",
    "Washington DC": "District of Columbia",
  };

  // Base location fees (only US for this component)
  const baseLocationFees: LocationFee[] = [
    {
      location: "United States (National Average)",
      fee: "$37,000",
      gdpIndicator: "$21T",
      gdpStatus: "High",
      globalStatus: "Top Tier",
      globalStatusIndicator: "5/5",
      statusBars: 2,
      population: "345.43M",
      country: "United States",
    },
  ];

  // Add US states as child entries under "United States (National Average)"
  const locationFees: LocationFee[] = baseLocationFees.reduce((acc: LocationFee[], entry) => {
    if (entry.location === "United States (National Average)") {
      acc.push(entry);
      usStatesByGdp.forEach((stateEntry) => {
        acc.push({
          location: stateEntry.state,
          fee: stateEntry.fee,
          gdpIndicator: `$${stateEntry.gdp}B`,
          gdpStatus: stateEntry.gdp >= 1000 ? "High" : stateEntry.gdp >= 300 ? "Medium" : "Low",
          globalStatus: stateEntry.gdp >= 1000 ? "Elite" : stateEntry.gdp >= 300 ? "Premium" : "Standard",
          globalStatusIndicator: stateEntry.gdp >= 1000 ? "4.5/5" : stateEntry.gdp >= 300 ? "4/5" : "3.5/5",
          statusBars: stateEntry.gdp >= 300 ? 2 : 1,
          isChild: true,
          state: stateEntry.state,
          country: "United States",
        });
      });
    } else {
      acc.push(entry);
    }
    return acc;
  }, []);

  // Add cities as child entries under their respective states
  const cities: LocationFee[] = [
    { location: "New York", fee: "$41,000", state: "New York", city: "New York", country: "United States" },
    { location: "Los Angeles", fee: "$41,000", state: "California", city: "Los Angeles", country: "United States" },
    { location: "San Francisco", fee: "$41,000", state: "California", city: "San Francisco", country: "United States" },
    { location: "Las Vegas", fee: "$41,000", state: "Nevada", city: "Las Vegas", country: "United States" },
    { location: "Miami", fee: "$41,000", state: "Florida", city: "Miami", country: "United States" },
    { location: "Chicago", fee: "$39,000", state: "Illinois", city: "Chicago", country: "United States" },
    { location: "Dallas", fee: "$39,000", state: "Texas", city: "Dallas", country: "United States" },
    { location: "Houston", fee: "$39,000", state: "Texas", city: "Houston", country: "United States" },
    { location: "Boston", fee: "$39,000", state: "Massachusetts", city: "Boston", country: "United States" },
    { location: "Washington DC", fee: "$39,000", state: "District of Columbia", city: "Washington DC", country: "United States" },
  ].map((cityEntry) => ({
    ...cityEntry,
    gdpIndicator: locationFees.find((loc) => loc.state === cityEntry.state)?.gdpIndicator || "$1T",
    gdpStatus: locationFees.find((loc) => loc.state === cityEntry.state)?.gdpStatus || "Medium",
    globalStatus: locationFees.find((loc) => loc.state === cityEntry.state)?.globalStatus || "Standard",
    globalStatusIndicator: locationFees.find((loc) => loc.state === cityEntry.state)?.globalStatusIndicator || "3.5/5",
    statusBars: locationFees.find((loc) => loc.state === cityEntry.state)?.statusBars || 1,
    isChild: true,
  }));

  // Combine all location fees (US states and cities only)
  const allLocationFees = [...locationFees, ...cities];

  // Filter locations based on search query
  const filteredLocations = React.useMemo(() => {
    return allLocationFees.filter((entry) => {
      if (!searchQuery) return false; // Hide all locations if no search query
      const query = searchQuery.toLowerCase();
      return (
        entry.location.toLowerCase().includes(query) ||
        (entry.state && entry.state.toLowerCase().includes(query)) ||
        (entry.city && entry.city.toLowerCase().includes(query)) ||
        (entry.country && entry.country.toLowerCase().includes(query))
      );
    });
  }, [searchQuery, allLocationFees]);

  const locationFactors: LocationFactor[] = [
    { factor: "Location GDP", className: "gdp" },
    { factor: "Location Population", className: "population" },
    { factor: "Location Global Position", className: "global-position" },
    { factor: <><CalendarOutlined style={{ marginRight: 8 }} aria-label="Age Indicator Icon" />Age</>, className: "age" },
    { factor: <><TeamOutlined style={{ marginRight: 8 }} aria-label="OLS Teacher OLS Inuo Indicator Icon" />OLS Teacher OLS Inuo</>, className: "ols-teacher" },
    { factor: <><StarOutlined style={{ marginRight: 8 }} aria-label="Experience/Status Indicator Icon" />Experience/Status</>, className: "experience-status" },
  ];

  return (
    <div className="section-container">
      <Title level={2} style={{ color: "#ffffff" }}>
        Location Pre-evaluation
      </Title>
      <Text style={{ color: "#ffffff", fontSize: "18px", fontWeight: 500 }}>
        Tuition fees are tailored based on key regional indicators:
      </Text>
      <ul className="factor-list">
        {locationFactors.map((factor, index) => (
          <li key={index} className={`factor-list-item ${factor.className}`}>
            {factor.factor}
          </li>
        ))}
      </ul>
      <Text style={{ color: "#ffffff" }}>
        Opt for "Fast Onboarding" with general tuition fees for quicker access to OLS programs.
      </Text>
      <Title level={3} style={{ color: "#ffffff", marginTop: 20 }}>
        Q2 2025 General Fees (Valid until June 30, 2025)
      </Title>
      <Tabs
        activeKey={activeTab}
        onChange={(key) => {
          setActiveTab(key);
          setSearchQuery(""); // Reset search query when switching tabs
        }}
        centered
      >
        <TabPane tab="United States" key="us">
          <div className="search-container">
            <span className="prompt-text">
              Search for a US state or city to view tuition fees:
            </span>
            <div style={{ display: "flex", alignItems: "center" }}>
              <Input
                className="styled-input"
                prefix={<SearchOutlined />}
                placeholder="e.g., California, New York"
                value={searchQuery}
                onChange={(e) => debouncedSetSearchQuery(e.target.value)}
                aria-label="Search for a US state or city"
              />
              {searchQuery && (
                <button
                  className="clear-button"
                  onClick={() => setSearchQuery("")}
                  aria-label="Clear search query"
                >
                  Clear Search
                </button>
              )}
            </div>
          </div>
          {searchQuery && filteredLocations.length > 0 ? (
            <Table
              columns={[
                {
                  title: "Location",
                  dataIndex: "location",
                  key: "location",
                  render: (text: string, record: LocationFee) => (
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <div className="status-bars" data-bars={record.statusBars}></div>
                      <span>{text}</span>
                    </div>
                  ),
                },
                {
                  title: "GDP Indicator",
                  dataIndex: "gdpIndicator",
                  key: "gdpIndicator",
                  render: (text: string, record: LocationFee) => (
                    <div>
                      <div style={{ fontSize: "16px", fontWeight: 600 }}>{text}</div>
                      <div style={{ fontSize: "12px", color: "#ccc" }}>{record.gdpStatus}</div>
                    </div>
                  ),
                },
                {
                  title: "Population",
                  dataIndex: "population",
                  key: "population",
                  render: (text: string) => (
                    <div style={{ fontSize: "16px", fontWeight: 600 }}>{text || "N/A"}</div>
                  ),
                },
                {
                  title: "Global Status",
                  dataIndex: "globalStatus",
                  key: "globalStatus",
                  render: (text: string, record: LocationFee) => (
                    <div>
                      <div style={{ fontSize: "16px", fontWeight: 600 }}>{text}</div>
                      <div style={{ fontSize: "12px", color: "#ccc" }}>{record.globalStatusIndicator}</div>
                    </div>
                  ),
                },
                { title: "General Fee", dataIndex: "fee", key: "fee" },
              ]}
              dataSource={filteredLocations}
              pagination={false}
              rowKey={(record) => record.location}
              rowClassName={(record) => (record.isChild ? "child-row" : "")}
            />
          ) : searchQuery ? (
            <Text style={{ color: "#ffffff" }}>No locations found for "{searchQuery}".</Text>
          ) : null}
          <div style={{ marginTop: 20 }}>
            <Button className="styled-button">
              <Link to="/register/olssregister" aria-label="Register as an OLS student">
                Register OLS student
              </Link>
            </Button>
          </div>
        </TabPane>
        <TabPane tab="Countries" key="countries">
          <CountriesIndicator searchQuery={searchQuery} onSearchQueryChange={setSearchQuery} />
        </TabPane>
      </Tabs>
    </div>
  );
};

export default LocationIndicator;